import { type Dispatch, type SetStateAction } from 'react';

import {
  ALIGNMENT_PROPERTY_ORIENTATION,
  DATE_FORMATS,
  FIELD_TYPES,
} from '../../../../shared/constants';
import {
  type DataProvider,
  type DefaultValue,
  type Field,
  type FieldElement,
  type OptionsFieldElement,
  type Row,
  type Section,
} from '../../../../shared/types';
import {
  hasAutoGeneration,
  hasColSpan,
  hasDefaultValue,
  hasHelperText,
  hasInfoTooltip,
  hasIsHidden,
  hasIsReadOnly,
  hasPasswordConfirmation,
  hasPlaceholder,
  hasValidations,
  isCheckboxField,
  isDateField,
  isField,
  isMultiSelectField,
  isRadioField,
  isRichTextField,
  isRow,
  isSection,
  isSingleSelectField,
  isTermsAndConditionsField,
  isTextareaField,
} from '../../../../shared/utils';
import { convertToDate } from '../../../../shared/utils/dateFormatUtils';
import { GROUP_FIELD_ORIENTAION, URL_VALIDATION } from '../constants';
import {
  type AttributeRow,
  type FieldPropertiesFormValues,
  type FieldRowContext,
} from '../types';

// Slug validation
export const validateSlug = (slug: string): boolean => {
  if (slug === 'create' || slug === '') {
    return false;
  }

  const regex = /^[a-z0-9-]+$/; // Allow lowercase letters, numbers, and hyphens

  return regex.test(slug);
};

// Convert file to logo string
export const imageToBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });

// URL Slug validation
export const validateUrlSlug = (input: string): string | null => {
  const trimmed = input.trim();

  if (!trimmed) {
    return URL_VALIDATION.ERROR_MESSAGES.EMPTY;
  }

  if (trimmed.length > URL_VALIDATION.MAX_LENGTH) {
    return URL_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  if (trimmed.includes(' ')) {
    return URL_VALIDATION.ERROR_MESSAGES.SPACES_NOT_ALLOWED;
  }

  if (!URL_VALIDATION.REGEX.test(trimmed)) {
    return URL_VALIDATION.ERROR_MESSAGES.INVALID_CHARACTERS;
  }

  return null;
};

/**
 * Clean up orphaned data providers when fields are removed
 */
export function cleanupOrphanedDataProviders(
  canvasFields: FieldElement[],
  dataProviders: Record<string, DataProvider>,
  setDataProviders: Dispatch<SetStateAction<Record<string, DataProvider>>>
): void {
  // Collect all dataProviderIds currently in use
  const usedProviderIds = new Set<string>();

  const collectProviderIds = (fields: FieldElement[]) => {
    fields.forEach((node) => {
      if (
        isField(node) &&
        (isSingleSelectField(node) ||
          isMultiSelectField(node) ||
          isRadioField(node) ||
          isCheckboxField(node))
      ) {
        if (node.dataProviderId) {
          usedProviderIds.add(node.dataProviderId);
        }
      }

      const kids = getChildren(node);

      if (kids.length > 0) {
        collectProviderIds(kids);
      }
    });
  };

  collectProviderIds(canvasFields);

  // Find orphaned providers
  const currentProviderIds = Object.keys(dataProviders);
  const orphanedProviders = currentProviderIds.filter(
    (id) => !usedProviderIds.has(id)
  );

  // Remove orphaned providers if any exist
  if (orphanedProviders.length > 0) {
    setDataProviders((prev: Record<string, DataProvider>) => {
      const orphanedSet = new Set(orphanedProviders);

      return Object.fromEntries(
        Object.entries(prev).filter(([id]) => !orphanedSet.has(id))
      );
    });
  }
}

const toLayout = (v: unknown): 'vertical' | 'horizontal' =>
  String(v).toLowerCase() === GROUP_FIELD_ORIENTAION.HORIZONTAL
    ? GROUP_FIELD_ORIENTAION.HORIZONTAL
    : GROUP_FIELD_ORIENTAION.VERTICAL;

/** Recursively replace target field in canvas */
export const updateFieldInCanvas = (
  fields: FieldElement[],
  targetId: string,
  updatedField: FieldElement
): FieldElement[] =>
  fields.map((field) => {
    // All FieldElements have id from intersection type
    if (field.id === targetId) {
      return updatedField;
    }

    // Only sections and rows have children
    if (isSection(field) && field.children) {
      return {
        ...field,
        children: updateFieldInCanvas(field.children, targetId, updatedField),
      } as Section;
    }

    if (isRow(field) && field.children) {
      return {
        ...field,
        children: updateFieldInCanvas(field.children, targetId, updatedField),
      } as Row;
    }

    return field;
  });

export const snapshotFromField = (
  f: FieldElement
): FieldPropertiesFormValues => {
  const validations = isField(f) && hasValidations(f) ? f.validations : [];

  const initialValues: FieldPropertiesFormValues = {
    // All FieldElements have fieldName and label from intersection type
    fieldName: f.fieldName,
    fieldLabel: f.label,
    placeholder:
      isField(f) && hasPlaceholder(f) ? (f.placeholder ?? null) : null,
    helperText: isField(f) && hasHelperText(f) ? (f.helperText ?? '') : '',
    infoTooltipText:
      isField(f) && hasInfoTooltip(f) ? (f.infoTooltipText ?? '') : '',
    colSpan: isField(f) && hasColSpan(f) ? f.colSpan : 1,
    isHidden: isField(f) && hasIsHidden(f) ? Boolean(f.isHidden) : false,
    isReadOnly: isField(f) && hasIsReadOnly(f) ? Boolean(f.isReadOnly) : false,
    isReadingEnforced: false,
    defaultValue:
      isField(f) && hasDefaultValue(f)
        ? f.defaultValue || { value: '', isExpression: false }
        : { value: '', isExpression: false },
    layout: GROUP_FIELD_ORIENTAION.VERTICAL,
    dateFormat: DATE_FORMATS.MM_DD_YYYY.display,
    minDate: null,
    maxDate: null,
    isPastDateAllowed: true,
    isFutureDateAllowed: true,
    isPasswordConfirmation:
      isField(f) && hasPasswordConfirmation(f)
        ? Boolean(f.advConfig.isPasswordConfirmation)
        : false,
    isAutoGenerationEnabled:
      isField(f) && hasAutoGeneration(f)
        ? Boolean(f.advConfig.isAutoGenerationEnabled)
        : false,
    validations,
    expressions: isField(f) && 'expressions' in f ? f.expressions : [],
    tncMessage: '',
    logoAlignment: ALIGNMENT_PROPERTY_ORIENTATION.CENTER,
  };

  // Only checkbox/radio support layout
  if (isField(f) && (isCheckboxField(f) || isRadioField(f))) {
    const opt = f as OptionsFieldElement;

    initialValues.layout = toLayout(
      opt.layout ?? GROUP_FIELD_ORIENTAION.VERTICAL
    );
  }

  // Only textarea has lineCount
  if (isField(f) && isTextareaField(f)) {
    initialValues.lineCount = f.lineCount;
  }

  // Only date fields have date-specific properties
  if (isField(f) && isDateField(f)) {
    const dateConfig = f.advConfig ?? {};

    initialValues.dateFormat =
      dateConfig.dateFormat ?? DATE_FORMATS.MM_DD_YYYY.display;
    initialValues.isPastDateAllowed = dateConfig.isPastDateAllowed ?? true;
    initialValues.isFutureDateAllowed = dateConfig.isFutureDateAllowed ?? true;

    // Convert string dates to Date objects if needed
    initialValues.minDate = convertToDate(dateConfig.minDate);
    initialValues.maxDate = convertToDate(dateConfig.maxDate);
  }

  // Rich text fields
  if (isField(f) && isRichTextField(f)) {
    initialValues.richText = f.advConfig?.richText;
  }

  // Terms and conditions fields
  if (isField(f) && isTermsAndConditionsField(f)) {
    const tncConfig = f.advConfig ?? {};

    initialValues.isChecked = f.isChecked;
    initialValues.richText = tncConfig.richText;

    initialValues.tncMessage = tncConfig.tncMessage;
    initialValues.tncType = tncConfig.tncType;
    initialValues.isReadingEnforced = tncConfig.isReadingEnforced;
    initialValues.linkUrl = tncConfig.linkUrl;
  }

  return initialValues; // <-- always return
};

export const areValuesEqual = (
  a: FieldPropertiesFormValues,
  b: FieldPropertiesFormValues
): boolean =>
  a.fieldLabel === b.fieldLabel &&
  a.fieldName === b.fieldName &&
  a.placeholder === b.placeholder &&
  a.helperText === b.helperText &&
  a.infoTooltipText === b.infoTooltipText &&
  a.lineCount === b.lineCount &&
  a.colSpan === b.colSpan &&
  a.isHidden === b.isHidden &&
  a.isReadOnly === b.isReadOnly &&
  a.isReadingEnforced === b.isReadingEnforced &&
  a.isPasswordConfirmation === b.isPasswordConfirmation &&
  a.isAutoGenerationEnabled === b.isAutoGenerationEnabled &&
  a.defaultValue.value === b.defaultValue.value &&
  a.defaultValue.isExpression === b.defaultValue.isExpression &&
  JSON.stringify(a.defaultValue.selected ?? []) ===
    JSON.stringify(b.defaultValue.selected ?? []) &&
  a.layout === b.layout &&
  a.dateFormat === b.dateFormat &&
  a.isPastDateAllowed === b.isPastDateAllowed &&
  a.isFutureDateAllowed === b.isFutureDateAllowed &&
  a.logoAlignment === b.logoAlignment &&
  a.tncType === b.tncType &&
  a.tncMessage === b.tncMessage &&
  a.linkUrl === b.linkUrl &&
  a.isChecked === b.isChecked &&
  ((a.minDate === null && b.minDate === null) ||
    (a.minDate !== null &&
      b.minDate !== null &&
      a.minDate.getTime() === b.minDate.getTime())) &&
  ((a.maxDate === null && b.maxDate === null) ||
    (a.maxDate !== null &&
      b.maxDate !== null &&
      a.maxDate.getTime() === b.maxDate.getTime())) &&
  JSON.stringify(a.validations ?? []) === JSON.stringify(b.validations ?? []) &&
  JSON.stringify(a.expressions ?? []) === JSON.stringify(b.expressions ?? []) &&
  JSON.stringify(a.richText ?? {}) === JSON.stringify(b.richText ?? {});

const getChildren = (node: FieldElement): FieldElement[] => {
  if (isSection(node)) {
    return node.children ?? [];
  }

  if (isRow(node)) {
    return node.children ?? [];
  }

  return [];
};

// Collect all nodes of type 'field' from an arbitrary tree shape.
// Looks in common child arrays: children, sections, rows, columns, fields.
// Excludes "spacer" fields so they never show up in MappingTable.
export const getAllFields = (nodes: FieldElement[]): Field[] => {
  const visit = (n: FieldElement): Field[] => {
    // Only include real fields, and skip spacers
    const includeSelf = isField(n) && n.fieldType !== FIELD_TYPES.SPACER;

    const selfPart = includeSelf ? [n] : [];
    const kidsPart = getChildren(n).flatMap(visit);

    return selfPart.concat(kidsPart);
  };

  return (Array.isArray(nodes) ? nodes : []).flatMap(visit);
};

// MappingTable
export const mapFieldsToRows = (fields: Field[]): AttributeRow[] =>
  fields.map((f) => ({
    id: f.id,
    fieldLabel: f.label,
    elementId: f.fieldName,
    type: f.fieldType,
    attributeValue: null,
    attributeSubtitle: null,
  }));

export const collectFieldRowContext = (
  tree: FieldElement[]
): FieldRowContext[] => {
  const out: FieldRowContext[] = [];
  let globalRowIndex = 0;
  let globalSectionIndex = 0;

  const visit = (
    node: FieldElement,
    ancestry: {
      rowId: string | null;
      rowIndex: number | null;
      sectionId: string | null;
      sectionIndex: number | null;
    }
  ) => {
    let next = ancestry;

    if (isSection(node)) {
      globalSectionIndex += 1;
      next = { ...next, sectionId: node.id, sectionIndex: globalSectionIndex };
    }

    if (isRow(node)) {
      globalRowIndex += 1;
      next = { ...next, rowId: node.id, rowIndex: globalRowIndex };
    }

    if (isField(node)) {
      out.push({
        fieldId: node.id,
        fieldName: node.fieldName,
        fieldLabel: node.label,
        rowId: next.rowId ?? null,
        rowIndex: next.rowIndex ?? null,
        sectionId: next.sectionId ?? null,
        sectionIndex: next.sectionIndex ?? null,
      });
    }

    getChildren(node).forEach((child) => visit(child, next));
  };

  (Array.isArray(tree) ? tree : []).forEach((n) =>
    visit(n, {
      rowId: null,
      rowIndex: null,
      sectionId: null,
      sectionIndex: null,
    })
  );

  return out;
};

export const normalizeElementType = (value: string): string =>
  value.replace(/[-_]/g, '').toUpperCase();

/**
 * Drops `defaultValue.selected` unless at least one entry has both non-empty
 * `value` and `label` (trimmed). Label-only and value-only rows are removed.
 * Used when saving the form and when syncing field properties to the canvas.
 */
export const sanitizeDropdownDefaultValueForSave = (
  defaultValue: DefaultValue | undefined | null
): DefaultValue => {
  if (defaultValue == null) {
    return { value: '', isExpression: false };
  }

  const { selected, ...rest } = defaultValue;

  if (!selected?.length) {
    return rest as DefaultValue;
  }

  const filtered = selected.filter(
    (entry) => entry.value.trim() !== '' && entry.label.trim() !== ''
  );

  if (filtered.length === 0) {
    return rest as DefaultValue;
  }

  return { ...rest, selected: filtered };
};
